import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from './Card';
import {
  adminApi,
  userApi,
  type TermsAcceptanceHistory as History
} from '../services/api';

type Props = {
  /** Admin view of another user; omitted for the signed-in user's own profile. */
  userId?: string | null;
  className?: string;
};

const formatDateTime = (value: string, language: string) =>
  new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'sv-SE', {
    dateStyle: 'long',
    timeStyle: 'short'
  }).format(new Date(value));

/** Which terms versions a user has accepted, newest first (ClickUp 869fcfjg9). */
export function TermsAcceptanceHistory({ userId, className = '' }: Props) {
  const { t, i18n } = useTranslation();
  const isAdminView = userId !== undefined;
  const [history, setHistory] = useState<History | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (isAdminView && !userId) return;
    let cancelled = false;
    setHistory(null);
    setFailed(false);
    (userId ? adminApi.getUserTermsAcceptances(userId) : userApi.getMyTermsAcceptances())
      .then((data) => {
        if (!cancelled) setHistory(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdminView, userId]);

  const current = history?.currentVersion ?? null;
  const latest = history?.acceptances[0] ?? null;
  const acceptedCurrent = Boolean(latest && current && latest.termsVersion.id === current.id);

  return (
    <Card
      className={`terms-acceptances ${className}`.trim()}
      title={t('termsVersions.history.title')}
      icon="file-text"
    >
      {isAdminView && !userId ? (
        <p className="text-muted mb-0">{t('termsVersions.history.noAccount')}</p>
      ) : failed ? (
        <div className="alert alert-danger mb-0" role="alert">
          {t('termsVersions.history.loadError')}
        </div>
      ) : !history ? (
        <div className="d-flex align-items-center gap-2 text-muted" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
          {t('common.loading')}
        </div>
      ) : (
        <>
          {latest && current && !acceptedCurrent && (
            <div className="alert alert-warning" role="status">
              {t('termsVersions.history.newerAvailable', { version: current.version })}
            </div>
          )}
          {history.acceptedWithoutVersion && (
            <div className="alert alert-info" role="status">
              {t('termsVersions.history.unknownVersion')}
            </div>
          )}
          {history.acceptances.length === 0 && !history.acceptedWithoutVersion && (
            <p className="text-muted mb-0">{t('termsVersions.history.none')}</p>
          )}
          {history.acceptances.length > 0 && (
            <ul className="list-unstyled mb-0 terms-acceptances__list">
              {history.acceptances.map((acceptance) => {
                const version = acceptance.termsVersion;
                const isCurrent = current?.id === version.id;
                return (
                  <li key={acceptance.id} className="terms-acceptances__item">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Link
                        to={`/anvandarvillkor?version=${encodeURIComponent(version.version)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fw-semibold text-break"
                      >
                        {version.title} · {t('termsVersions.versionLabel', { version: version.version })}
                      </Link>
                      <span className={`badge ${isCurrent ? 'bg-soft-success text-success' : 'bg-soft-secondary text-secondary'}`}>
                        {isCurrent ? t('termsVersions.history.current') : t('termsVersions.history.previous')}
                      </span>
                    </div>
                    <div className="small text-muted mt-1">
                      <time dateTime={acceptance.acceptedAt}>
                        {t('termsVersions.history.acceptedOn', {
                          date: formatDateTime(acceptance.acceptedAt, i18n.language)
                        })}
                      </time>
                      {' · '}
                      {t(`termsVersions.context.${acceptance.context}`)}
                      {isAdminView && (
                        <>
                          {' · '}
                          {t(`termsVersions.persona.${acceptance.personaType}`, acceptance.personaType)}
                        </>
                      )}
                    </div>
                    {isAdminView && (
                      <div className="small text-muted text-break terms-acceptances__checksum">
                        {t('termsVersions.checksum')}: <code>{version.sha256}</code>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}
